/* eslint-disable camelcase */
import type { FilterType, DateRange, GlobalFilter } from '@hotjar/global-filters';
import { FilterKindEnum, LargeListFilterOperation } from '@hotjar/global-filters';
import { moment } from '@hotjar/insights-common/lib/moment';
import { delimit } from '../../../../../common/Responses/Filters/Query/queryUtils';
import {
  FROM,
  DATE_FORMAT,
  TO,
  LAST,
  ALL,
} from '../../../../../common/Responses/Filters/Query/GlobalFilterQuery/constants';
import { encodeUserAttribute } from './encodeUserAttribute';
import type { DateQuery, GlobalFilterQuery, OtherKeys } from './GlobalFilterQuery';

const defaultQuery: GlobalFilterQuery = {
  date: undefined,
  device: undefined,
  country: undefined,
  country_not: undefined,
  user_attribute: undefined,
};

function getQueryKey(filter: FilterType): OtherKeys | undefined {
  if (filter.value === undefined) return undefined;
  switch (filter.kind) {
    case FilterKindEnum.DeviceType:
      return 'device';
    case FilterKindEnum.Country:
      switch (filter.value.operation) {
        case LargeListFilterOperation.IS_ANY_OF_THESE:
          return 'country';
        case LargeListFilterOperation.IS_NONE_OF_THESE:
          return 'country_not';
      }
    case FilterKindEnum.UserAttribute:
      return 'user_attribute';
  }
}

function getQueryDateValue(date: DateRange | undefined): DateQuery | undefined {
  if (date === undefined) return undefined;
  switch (date.type) {
    case 'absolute':
      return {
        date: delimit(
          FROM,
          moment(date.from).format(DATE_FORMAT),
          TO,
          moment(date.to).format(DATE_FORMAT),
        ),
      };
    case 'relative':
      return { date: delimit(LAST, date.amount.toString(), date.unit) };
    case 'all':
      return { date: ALL };
  }
}

function getQueryValue(filter: FilterType): string | undefined {
  if (filter.value === undefined) return undefined;

  switch (filter.kind) {
    case FilterKindEnum.DeviceType:
      return delimit(...filter.value.sort());
    case FilterKindEnum.Country:
      return delimit(...filter.value.selectedValues.sort());
    case FilterKindEnum.UserAttribute:
      return encodeUserAttribute(filter);
  }
}

function getQuery(filter: FilterType): [OtherKeys, string] | undefined {
  const key = getQueryKey(filter);
  if (key === undefined) return undefined;
  const value = getQueryValue(filter);
  if (value === undefined) return undefined;
  return [key, value];
}

export const convertGlobalFiltersToQuery = (globalFilters: GlobalFilter): GlobalFilterQuery => {
  return {
    ...defaultQuery,
    ...getQueryDateValue(globalFilters.dateRange),
    ...globalFilters.filterList.reduce<Partial<GlobalFilterQuery>>((previous, filter) => {
      const query = getQuery(filter);

      if (query === undefined) return previous;

      const [key, value] = query;

      const list = previous[key];
      if (list === undefined) {
        previous[key] = [value];
        return previous;
      }

      list.push(value);
      return previous;
    }, {}),
  };
};
